import { cloneElement, isValidElement, useId, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { type Authority, reasonFor } from "../../auth/authorities";
import { useAccess } from "../../auth/useAccess";

/**
 * Wraps a control the operator may not be allowed to use.
 *
 * Visible but inert, with the reason — never hidden. Hiding makes "why can't I
 * see the button my colleague has?" unanswerable, and it makes the console's
 * shape depend on who is looking, so two operators comparing screens cannot.
 *
 * `aria-disabled`, NOT the `disabled` attribute: a disabled control is not
 * focusable and is skipped by screen readers, so the explanation is withheld
 * from exactly the person who most needs it. The reason also renders as
 * VISIBLE text rather than only a tooltip — tooltips are invisible on touch,
 * unreachable by keyboard, and absent from the screenshot someone pastes into
 * Slack asking for access.
 *
 * WHAT ACTUALLY STOPS THE WRITE is the capture-phase guard below, not CSS.
 * This used to hold `pointer-events-none` on the wrapper and claim it
 * "swallows activation" — it swallows MOUSE activation. The child stayed in the
 * tab order (`tabIndex={-1}` on a wrapper does nothing to its descendants), so
 * Tab-to-it plus Enter — which browsers deliver as a click on a focused button
 * — ran the handler. With the gateway's group check not yet live, that was the
 * only thing standing between an operator and a write they do not hold:
 * `quota:force` was reachable by keyboard from the 409 override in
 * GuardRefused. React's synthetic capture phase runs ancestor-first, so
 * stopping the event here means it never reaches the child's `onClick`,
 * whatever the child is and however it was activated.
 */
export function Gate({
  need,
  children,
  className = "",
}: {
  need: Authority;
  children: ReactNode;
  className?: string;
}) {
  const access = useAccess();
  const reasonId = useId();
  if (access.can(need)) return <>{children}</>;

  const reason = reasonFor(need, access);

  const stop = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * The state and the reason belong ON THE CONTROL, which is the only thing a
   * screen reader focuses. `aria-disabled` on the wrapper was neither valid
   * (a role-less div) nor inherited, and nothing linked the reason to the
   * button — so an assistive-tech user met an ordinary enabled button with no
   * explanation, the exact outcome this component exists to prevent.
   *
   * Gate takes ONE interactive control. Wrapping a container would put
   * `aria-disabled` on a generic, where ARIA prohibits it — so a call site with
   * several controls gates the one that writes (see TierControl, where the tier
   * Select stays live because choosing a target writes nothing).
   */
  const control = isValidElement(children)
    ? cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        "aria-disabled": true,
        "aria-describedby": reasonId,
      })
    : children;

  return (
    <div className={className}>
      <div
        className="opacity-45 grayscale [&_*]:cursor-not-allowed"
        onClickCapture={stop}
        onKeyDownCapture={(e) => {
          // Enter and Space are what activate a focused button or link.
          if (e.key === "Enter" || e.key === " ") stop(e);
        }}
      >
        {control}
      </div>
      <p id={reasonId} className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
        {reason}{" "}
        <Link to="/governance/permissions" className="underline">
          Your access
        </Link>
      </p>
    </div>
  );
}

/**
 * The server refused a write this map said was allowed.
 *
 * A real scenario, not a hypothetical: group membership takes effect on the
 * NEXT token, so a user added to a group minutes ago holds one that predates
 * it. Without naming that, the refusal reads as a bug in the console.
 */
export function ForbiddenNotice({ message }: { message: string }) {
  const access = useAccess();
  return (
    <div className="rounded-md border border-slate-950/10 dark:border-white/10 bg-amber-50 dark:bg-amber-400/10 p-4 text-sm text-amber-700 dark:text-amber-400">
      <p className="font-medium">The server refused this</p>
      <p className="mt-1">{message}</p>
      <p className="mt-2 text-xs">
        Your token says you are in {access.groups.join(", ") || "no group"}. Group
        membership only takes effect on your next token — if you were added to a group
        recently, sign out and back in.
      </p>
    </div>
  );
}
